(function (root) {
  'use strict';
  const MAX_LINES = 30, MAX_QUANTITY = 20, MAX_TOTAL = 100, MAX_AGE = 30 * 86400;
  const localeOK = value => /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?$/i.test(value);
  function unsupported(line) {
    if (line.properties && Object.keys(line.properties).length) return 'line_properties';
    if (line.selling_plan_allocation || line.selling_plan) return 'selling_plan';
    if ((line.item_components && line.item_components.length) || line.parent_relationship ||
        (line.components && line.components.length)) return 'bundle_components';
    if (line.price === 0 || line.final_price === 0 || line.gift_card ||
        /free[ _-]?gift|shipping[ _-]?protection|insurance/i.test([line.product_type, line.product_title, line.name, line.title].join(' '))) return 'gift_or_protection';
    return '';
  }
  function absoluteURL(value, origin, productOnly) {
    if (typeof value !== 'string' || !value) return '';
    try {
      const url = new URL(value, origin);
      if (url.protocol !== 'https:') return '';
      if (productOnly && (url.origin !== origin || !url.pathname.includes('/products/'))) return '';
      return url.href;
    } catch (_) { return ''; }
  }
  function parse(value, now) {
    if (typeof value !== 'string' || value.length > 2048 || !/^[23]\.\d{10}\.[\d:,]+$/.test(value)) throw new Error('invalid');
    const parts = value.split('.'), issued = Number(parts[1]);
    if (issued > now + 120 || now - issued > MAX_AGE) throw new Error('expired');
    const seen = new Set(), lines = parts[2].split(',').map(pair => {
      if (!/^[1-9]\d{0,15}:[1-9]\d?$/.test(pair)) throw new Error('invalid');
      const [id, quantity] = pair.split(':').map(Number);
      if (!Number.isSafeInteger(id) || quantity > MAX_QUANTITY || seen.has(id)) throw new Error('invalid');
      seen.add(id); return { id, quantity };
    });
    if (lines.length > MAX_LINES || lines.reduce((sum, line) => sum + line.quantity, 0) > MAX_TOTAL) throw new Error('invalid');
    if (parts[0] === '3') lines.extrasExcluded = true;
    return lines;
  }
  function normalize(added, items, options) {
    const result = { CartRecoverySchema: 2, CartRecoverySupported: false, CartURL: '', CartItems: [] };
    if (!added || typeof added !== 'object') return result;
    const origin = options.origin;
    result.Name = added.product_title || added.title || '';
    result.ImageURL = absoluteURL(added.image || (added.featured_image && added.featured_image.url), origin, false);
    result.ProductURL = absoluteURL(added.url, origin, true);
    result.URL = result.ProductURL;
    const cents = added.final_price == null ? added.price : added.final_price;
    if (typeof cents === 'number' && Number.isFinite(cents) && cents >= 0) result.Price = cents / 100;
    result.Currency = options.currency || '';
    const reject = reason => Object.assign(result, { CartRecoveryUnsupportedReason: reason });
    if (!Array.isArray(items) || !items.length) return reject('missing_complete_cart');
    if (options.pendingBundle) return reject('multi_add_pending');
    const quantities = new Map();
    let excludedExtras = 0;
    for (const line of items) {
      if (!line || !Number.isSafeInteger(line.variant_id) || line.variant_id <= 0 ||
          !Number.isSafeInteger(line.quantity) || line.quantity <= 0) return reject('invalid_cart_line');
      const reason = unsupported(line);
      if (reason === 'gift_or_protection' || (reason === 'line_properties' && (line.price === 0 || line.final_price === 0 || line.gift_card || /free[ _-]?gift|shipping[ _-]?protection|insurance/i.test([line.product_type, line.product_title, line.title].join(' '))))) { excludedExtras++; continue; }
      if (reason) return reject(reason);
      quantities.set(line.variant_id, (quantities.get(line.variant_id) || 0) + line.quantity);
    }
    if (!quantities.has(added.variant_id || added.id)) return reject('added_line_missing_from_snapshot');
    const localeRoot = options.localeRoot || '/';
    if (!localeOK(localeRoot)) return reject('invalid_locale');
    const issued = options.now == null ? Math.floor(Date.now() / 1000) : options.now;
    const value = (excludedExtras ? '3.' : '2.') + issued + '.' + Array.from(quantities, ([id, quantity]) => id + ':' + quantity).join(',');
    let lines;
    try { lines = parse(value, issued); } catch (_) { return reject('cart_limits'); }
    result.CartURL = origin + localeRoot + 'cart#bc-restore=' + value;
    result.CartItems = lines.map(line => ({ VariantID: line.id, Quantity: line.quantity }));
    result.CartRecoverySupported = true;
    result.CartRecoveryPartial = excludedExtras > 0;
    result.CartRecoveryExcludedExtras = excludedExtras;
    if (excludedExtras) result.CartRecoverySchema = 3;
    return result;
  }
  async function restore(lines, api) {
    let failures = 0;
    for (const target of lines) {
      const cart = await api.cart();
      if (!cart || !Array.isArray(cart.items)) throw new Error('cart_unavailable');
      const matches = cart.items.filter(line => line.variant_id === target.id);
      // Never reinterpret a personalized, subscription, gift or protection line as a plain product.
      if (matches.some(unsupported)) { failures++; continue; }
      const current = matches.reduce((sum, line) => sum + line.quantity, 0);
      if (!Number.isSafeInteger(current) || current < 0) throw new Error('cart_unavailable');
      if (current >= target.quantity) continue;
      try {
        const variant = await api.variant(target.id);
        if (!variant || variant.id !== target.id || variant.available !== true || unsupported(variant)) { failures++; continue; }
        // Send only a variant ID and a positive deficit. Shopify controls price and discounts.
        await api.add(target.id, target.quantity - current);
      } catch (_) {
        // Inventory errors can partially add stock. Do not blindly retry the same deficit.
        failures++;
      }
    }
    const finalCart = await api.cart();
    if (!finalCart || !Array.isArray(finalCart.items)) throw new Error('cart_unavailable');
    if (lines.some(target => finalCart.items.filter(line => line.variant_id === target.id && !unsupported(line))
      .reduce((sum, line) => sum + line.quantity, 0) < target.quantity)) failures++;
    return failures ? 'partial' : 'complete';
  }
  if (typeof module === 'object' && module.exports) {
    module.exports = normalize; Object.assign(module.exports, { parse, restore, unsupported });
  }
  if (!root || !root.document || root.BCCartRecoveryInstalled) return;
  root.BCCartRecoveryInstalled = true;
  root.BCCartRecovery = Object.freeze({ normalize: normalize });
  const previousCallback = root.addToCartCallBack;
  root.addToCartCallBack = function (item, items) {
    let previousResult;
    if (typeof previousCallback === 'function') previousResult = previousCallback.apply(this, arguments);
    if (!item || typeof item !== 'object') return previousResult;
    const selection = root.mu_product_upsells_selection;
    const fields = normalize(item, items, {
      origin: root.location.origin,
      localeRoot: root.Shopify && root.Shopify.routes && root.Shopify.routes.root,
      currency: root.Shopify && root.Shopify.currency && root.Shopify.currency.active,
      pendingBundle: Boolean(selection && selection.formDataArray && selection.formDataArray.length > 1)
    });
    Object.assign(item, fields);
    if (root.BCCartRecoveryPreview) root.BCCartRecoveryPreviewPayload = fields;
    return previousResult;
  };
  function initializeCartRecovery() {
  const localeRoot = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
  if (!localeOK(localeRoot) || root.location.pathname !== localeRoot + 'cart') return;
  const messages = {
    complete: 'Your saved items are back in your cart. Items and larger quantities already here were kept. Please review your cart; current prices and offers apply.',
    extras: 'Your saved regular products are back in your cart. Existing items and larger quantities were kept. Please reselect any missing gifts and shipping protection; gift eligibility and current prices and offers apply.',
    partial: 'Please reselect any missing gifts and shipping protection. Some saved items could not be restored automatically because they are unavailable or need special options. Your existing cart was kept. Please review the items and quantities below before checkout.',
    missing: 'This reminder does not include a saved cart link. Your current cart is shown below. Please add any missing items again.',
    expired: 'This saved cart link has expired. Your current cart was kept. Please choose your items again or contact us for help.',
    invalid: 'This saved cart link is incomplete or unsupported. Your current cart was kept. Please choose your items again or contact us for help.',
    error: 'We could not finish restoring your saved items. Your existing cart was kept, and some saved items may have been added. Please review the cart below before trying again.'
  };
  function banner(text) {
    const element = root.document.createElement('div');
    element.id = 'bc-cart-recovery-status'; element.setAttribute('role', 'status');
    element.setAttribute('aria-live', 'polite'); element.textContent = text;
    element.style.cssText = 'padding:18px;margin:16px auto;max-width:1000px;border:2px solid #285144;background:#fff;color:#14291f;font:16px/1.5 Arial,sans-serif;';
    const main = root.document.getElementById('MainContent') || root.document.body;
    main.prepend(element);
  }
  const status = new URL(root.location.href).searchParams.get('bc_recovery');
  if (Object.prototype.hasOwnProperty.call(messages, status)) banner(messages[status]);
  async function request(path, body) {
    const controller = new AbortController(), timer = root.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await root.fetch(localeRoot + path, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { 'Accept': 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}), signal: controller.signal
      });
      if (!response.ok) throw new Error('request_failed');
      return await response.json();
    } finally { root.clearTimeout(timer); }
  }
  async function run() {
    let result;
    try {
      const lines = parse(root.location.hash.slice('#bc-restore='.length), Math.floor(Date.now() / 1000));
      banner('Restoring your saved items while keeping your current cart. Please wait…');
      result = await restore(lines, {
        cart: () => request('cart.js'),
        variant: id => request('variants/' + id + '.js'),
        add: (id, quantity) => request('cart/add.js', { items: [{ id, quantity }] })
      });
      if (result === 'complete' && lines.extrasExcluded) result = 'extras';
    } catch (error) { result = error.message === 'expired' || error.message === 'invalid' ? error.message : 'error'; }
    // Refresh both the theme cart and app drawer. Preserve other query parameters, including preview.
    const destination = new URL(root.location.href);
    destination.hash = ''; destination.searchParams.set('bc_recovery', result);
    root.location.replace(destination.href);
  }
  let recovering = false;
  function begin() {
    if (recovering || !root.location.hash.startsWith('#bc-restore=')) return;
    recovering = true;
    root.BCCartRecoveryRestoring = true;
    const locks = root.navigator && root.navigator.locks;
    if (locks) locks.request('bc-cart-recovery', run).catch(() => banner(messages.error));
    else run();
  }
  root.addEventListener('hashchange', begin);
  begin();
  }
  if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', initializeCartRecovery, { once: true });
  else initializeCartRecovery();
})(typeof window === 'undefined' ? null : window);
